'use client';

import { useContext, useEffect, useRef, useState } from 'react';

import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';

/**
 * Announces the current slide to screen reader users.
 *
 * Activating a nav button leaves focus on that button, so without a live region
 * nothing tells a screen reader user that the slide changed — the dots are
 * decorative and the newly revealed slide's label is not read [CDS-5858].
 *
 * Rendered by `PhotoCarousel.Root`.
 *
 * @since 10.0.0
 */
export const PhotoCarouselLiveAnnouncer = () => {
  const { activeIndex = 0, totalSlides = 0 } = useContext(PhotoCarouselRootContext);

  const [announcement, setAnnouncement] = useState('');
  const lastAnnouncedIndexRef = useRef(activeIndex);

  useEffect(() => {
    /*
     * Stay silent on mount. A search results page renders many carousels, and
     * announcing every one of them on load would bury everything else. This also
     * keeps the server-rendered markup identical to the first client render.
     */
    if (lastAnnouncedIndexRef.current === activeIndex) return;

    lastAnnouncedIndexRef.current = activeIndex;
    setAnnouncement(totalSlides > 1 ? `Slide ${activeIndex + 1} of ${totalSlides}` : '');
  }, [activeIndex, totalSlides]);

  if (totalSlides < 2) return null;

  return (
    <VisuallyHidden
      aria-atomic
      aria-live="polite"
      className="c11n_photo-carousel__live-announcer"
      data-c11n-component="PhotoCarousel.LiveAnnouncer"
      data-c11n-version={__C11N_VERSION__}
    >
      {announcement}
    </VisuallyHidden>
  );
};

PhotoCarouselLiveAnnouncer.displayName = 'PhotoCarousel.LiveAnnouncer';
